import { type ComponentType, useState } from 'react';
import { Link, useLocation } from 'react-router';

import {
  ChevronRightIcon,
  ImageIcon,
  LightningBoltIcon,
  Link2Icon,
  SwitchIcon,
} from '@radix-ui/react-icons';

import {
  Badge,
  Button,
  cn,
  Collapsible,
  CollapsibleTrigger,
  FolderGit2Icon,
  GitMergeIcon,
  PyramidIcon,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  UsersIcon,
} from '@app-evolution-cms/element-ui';

import { AnimatePresence, motion } from 'motion/react';

import { MODULE_CLAIM } from '../../users/_constants/module-claim';

const MODULE_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  [MODULE_CLAIM.modules]: PyramidIcon,
  [MODULE_CLAIM.media]: ImageIcon,
  [MODULE_CLAIM.rules]: GitMergeIcon,
  [MODULE_CLAIM.ruleGroups]: FolderGit2Icon,
  [MODULE_CLAIM.ruleEvents]: LightningBoltIcon,
  [MODULE_CLAIM.featureFlags]: SwitchIcon,
  [MODULE_CLAIM.targetGroups]: UsersIcon,
  [MODULE_CLAIM.eventsApplicationUrl]: Link2Icon,
};

interface MenuItemProps {
  id: string;
  label: string;
  role: string;
  hasCreate: boolean;
  hasUpload: boolean;
}

export function MenuItem({ id, label, role, hasCreate, hasUpload }: MenuItemProps) {
  const { pathname, search } = useLocation();
  const base = pathname?.split('/')[1] ?? '';
  const action = (pathname?.split('/')[2] ?? 'main') as 'create' | 'edit' | 'main' | 'upload';
  const createKind = new URLSearchParams(search).get('kind');

  const Icon = MODULE_ICONS[id];
  const [isOpen, setIsOpen] = useState(id === base);

  return (
    <Collapsible asChild className="group/collapsible" open={isOpen} onOpenChange={setIsOpen}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            asChild
            tooltip={{
              children: label,
              className: 'max-w-72',
            }}
          >
            <Button
              id={`PER-navigation-sidebarContent-${id}`}
              variant="ghost"
              className={cn('pr-3', {
                'bg-primary! text-primary-foreground': id === base,
              })}
            >
              <Badge className="opacity-70" size="sm">
                {role.trim()}
              </Badge>
              <div className="flex items-center gap-2">
                {Icon && <Icon />}
                <div className="grid">
                  <div className="truncate">{label}</div>
                </div>
              </div>
              <ChevronRightIcon className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </Button>
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              <SidebarMenuSub>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild>
                    <Link
                      className="rounded-3xl"
                      id={`PER-navigation-sidebarContent-${id}-manage`}
                      to={{
                        pathname: `/${id}`,
                        search: id === base && action === 'main' ? search : undefined,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        {id === base && (action === 'main' || action === 'edit') ? (
                          <span className="size-2 rounded-full bg-primary" />
                        ) : null}
                        Gestionar
                        {id === base && action === 'edit' ? (
                          <Badge size="sm" className="py-0 font-semibold">
                            editando
                          </Badge>
                        ) : null}
                      </div>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                {hasCreate ? (
                  id === MODULE_CLAIM.rules ? (
                    <>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild>
                          <Link
                            className="rounded-3xl"
                            id={`PER-navigation-sidebarContent-${id}-create-rule`}
                            to={`${id}/create?kind=rule`}
                          >
                            <div className="flex items-center gap-2">
                              {id === base && action === 'create' && createKind === 'rule' ? (
                                <span className="size-2 rounded-full bg-primary" />
                              ) : null}
                              Crear regla
                            </div>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild>
                          <Link
                            className="rounded-3xl"
                            id={`PER-navigation-sidebarContent-${id}-create-rule-with-variants`}
                            to={`${id}/create?kind=rule-with-variants`}
                          >
                            <div className="flex items-center gap-2">
                              {id === base &&
                              action === 'create' &&
                              createKind === 'rule-with-variants' ? (
                                <span className="size-2 rounded-full bg-primary" />
                              ) : null}
                              Crear regla con variantes
                            </div>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </>
                  ) : (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild>
                        <Link
                          className="rounded-3xl"
                          id={`PER-navigation-sidebarContent-${id}-create`}
                          to={`${id}/create`}
                        >
                          <div className="flex items-center gap-2">
                            {id === base && action === 'create' ? (
                              <span className="size-2 rounded-full bg-primary" />
                            ) : null}
                            Crear
                          </div>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )
                ) : null}
                {hasUpload ? (
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild>
                      <Link
                        className="rounded-3xl"
                        id={`PER-navigation-sidebarContent-${id}-upload`}
                        to={`${id}/upload`}
                      >
                        <div className="flex items-center gap-2">
                          {id === base && action === 'upload' ? (
                            <span className="size-2 rounded-full bg-primary" />
                          ) : null}
                          {id === MODULE_CLAIM.media ||
                          id === MODULE_CLAIM.notificationCenterTemplates
                            ? 'Subir'
                            : 'Importar'}
                        </div>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ) : null}
              </SidebarMenuSub>
            </motion.div>
          )}
        </AnimatePresence>
      </SidebarMenuItem>
    </Collapsible>
  );
}
